import { logger } from "@/lib/logger";
import { useState, useEffect, useCallback } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  FlaskConical, Loader2, TrendingUp, TrendingDown, Users, DollarSign,
  Target, Eye, MousePointer, ShoppingCart, AlertTriangle, CheckCircle,
  BarChart3, Zap, Shield, Bot, Sparkles, Building2, ShieldQuestion,
  Image as ImageIcon, PenTool, LayoutGrid, Search, Filter, Gauge, Repeat,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const t = localStorage.getItem("roas_session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const PLATFORMS = {
  google: { name: "Google Ads", color: "#4285F4" },
  meta: { name: "Meta for Business Ads", color: "#1877F2" },
  linkedin: { name: "LinkedIn Ads", color: "#0A66C2" },
  bing: { name: "Bing Ads", color: "#00A4EF" },
};

// Static metadata for the 10 evaluation agents returned by the multi-agent worker
const PERSONAS = {
  agent_01_buyer_icp: { name: "Enterprise ICP Buyer", role: "B2B Decision Maker & Budget Owner", icon: Building2 },
  agent_02_skeptical_consumer: { name: "Skeptical Consumer", role: "Risk-Averse Ad Skeptic", icon: ShieldQuestion },
  agent_03_creative_hook: { name: "Creative & Visual Hook", role: "Creative Director & Attention Specialist", icon: ImageIcon },
  agent_04_copy_tone: { name: "Copy & Tone Analyst", role: "Direct Response Copywriter", icon: PenTool },
  agent_05_meta_evaluator: { name: "Meta Auction & Feed", role: "Meta Ads Algorithmic Specialist", icon: LayoutGrid },
  agent_06_google_intent: { name: "Google Search/Intent", role: "Search Congruence Specialist", icon: Search },
  agent_07_linkedin_ranker: { name: "LinkedIn Ranker", role: "B2B Executive Persona Analyst", icon: Users },
  agent_08_funnel_cro: { name: "Funnel & CRO", role: "Conversion Rate Optimization Auditor", icon: Filter },
  agent_09_budget_bid: { name: "Budget & Bid Efficiency", role: "Media Buyer & Pacing Auditor", icon: Gauge },
  agent_10_retention_ltv: { name: "Retention & LTV", role: "Lifetime Value & Cohort Analyst", icon: Repeat },
};

const VERDICT_STYLES = {
  STRONG_FIT: { label: "Strong Fit", badge: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", bar: "bg-emerald-500" },
  MODERATE: { label: "Moderate", badge: "bg-amber-500/10 text-amber-400 border-amber-500/20", bar: "bg-amber-500" },
  WEAK: { label: "Weak", badge: "bg-orange-500/10 text-orange-400 border-orange-500/20", bar: "bg-orange-500" },
  CRITICAL_RISK: { label: "Critical Risk", badge: "bg-red-500/10 text-red-400 border-red-500/20", bar: "bg-red-500" },
};

const RISK_LABELS = {
  budget_bleeding: "Budget Bleeding",
  creative_fatigue: "Creative Fatigue",
  audience_overlap: "Audience Overlap",
  bidding_inefficiency: "Bidding Inefficiency",
};

const OBJECTIVES = ["conversions", "leads", "sales", "traffic", "awareness", "app_installs"];
const BIDDING_STRATEGIES = ["maximize_conversions", "maximize_clicks", "target_cpa", "target_roas", "manual_cpc"];
const MEDIA_TYPES = ["image", "video", "carousel", "text"];

const toList = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

function MetricCard({ icon: Icon, label, value, color, sub }) {
  return (
    <div className="bg-[#121212] border border-white/10 rounded-xl p-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-zinc-400 text-xs">{label}</span>
        <Icon className={cn("w-4 h-4", color || "text-zinc-400")} />
      </div>
      <p className="text-white text-xl font-bold">{value}</p>
      {sub && <p className="text-zinc-500 text-xs mt-0.5">{sub}</p>}
    </div>
  );
}

function AgentCard({ agent, index }) {
  const meta = PERSONAS[agent.agent_id] || { name: agent.agent_id, role: "AI Evaluator", icon: Bot };
  const Icon = meta.icon;
  const verdict = VERDICT_STYLES[agent.verdict] || VERDICT_STYLES.MODERATE;
  const rating = Math.max(0, Math.min(100, Number(agent.rating) || 0));
  return (
    <motion.div
      data-testid={`agent-insight-${agent.agent_id}`}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="bg-[#121212] border border-white/10 rounded-xl p-4 flex flex-col"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-violet-300" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-white text-sm font-semibold truncate">{meta.name}</p>
          <p className="text-zinc-500 text-xs truncate">{meta.role}</p>
        </div>
        <Badge className={cn("text-[10px] border shrink-0", verdict.badge)}>{verdict.label}</Badge>
      </div>
      <div className="mb-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-zinc-500 text-xs">Rating</span>
          <span className="text-white text-xs font-bold">{rating}/100</span>
        </div>
        <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            className={cn("h-full rounded-full", verdict.bar)}
            initial={{ width: 0 }}
            animate={{ width: `${rating}%` }}
            transition={{ duration: 0.8, delay: index * 0.05 }}
          />
        </div>
      </div>
      <p className="text-zinc-400 text-xs leading-relaxed">{agent.feedback}</p>
    </motion.div>
  );
}

export default function SimulatorPage({ user }) {
  const [engine, setEngine] = useState("multiagent"); // "multiagent" | "classic"

  // ---- shared: campaigns list ----
  const [campaigns, setCampaigns] = useState([]);

  // ---- classic (GPT-4o) state ----
  const [mode, setMode] = useState("existing");
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [platform, setPlatform] = useState("google");
  const [form, setForm] = useState({ campaign_name: "", campaign_description: "", target_audience: "", daily_budget: "50", creative_text: "" });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  // ---- multi-agent state ----
  const [maMode, setMaMode] = useState("custom");
  const [maCampaign, setMaCampaign] = useState("");
  const [maRunning, setMaRunning] = useState(false);
  const [maResult, setMaResult] = useState(null);
  const [maForm, setMaForm] = useState({
    campaign_name: "",
    objective: "conversions",
    platforms: ["google"],
    total_budget: "5000",
    daily_budget: "250",
    bidding_strategy: "maximize_conversions",
    geos: "US",
    age_range: "25-45",
    interests: "",
    job_titles: "",
    headline: "",
    primary_text: "",
    call_to_action: "Learn More",
    media_type: "image",
    media_url: "",
    landing_page_url: "",
  });

  const fetchCampaigns = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/simulator/campaigns`, { credentials: "include", headers: authHeaders() });
      if (resp.ok) setCampaigns(await resp.json());
    } catch (e) { logger.error("Error:", e); }
  }, []);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const availablePlatforms = Object.entries(PLATFORMS).filter(([k]) => user?.is_demo || k !== "meta");

  // Prefill creative fields from a selected existing campaign so the evaluation is runnable in one click
  const handleSelectMaCampaign = (cid) => {
    setMaCampaign(cid);
    const c = campaigns.find((x) => x.campaign_id === cid);
    if (!c) return;
    setMaForm((f) => ({
      ...f,
      headline: f.headline || c.name || "",
      primary_text: f.primary_text || `Discover ${c.name}. See why marketers choose us and start today.`,
    }));
  };

  // ---------- classic run ----------
  const runSimulation = async () => {
    setRunning(true);
    setResult(null);
    try {
      const body = mode === "existing"
        ? { mode: "existing", campaign_id: selectedCampaign, platform }
        : { mode: "custom", platform, ...form, daily_budget: parseFloat(form.daily_budget) || 50 };

      const resp = await fetch(`${API}/simulator/run`, {
        method: "POST", credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (resp.ok) {
        setResult(await resp.json());
        toast.success("Simulation complete!");
      } else {
        const err = await resp.json();
        toast.error(err.detail || "Simulation failed");
      }
    } catch { toast.error("Simulation failed"); }
    finally { setRunning(false); }
  };

  // ---------- multi-agent run ----------
  const togglePlatform = (key) => {
    setMaForm((f) => {
      const has = f.platforms.includes(key);
      const next = has ? f.platforms.filter((p) => p !== key) : [...f.platforms, key];
      return { ...f, platforms: next.length ? next : f.platforms };
    });
  };

  const runMultiAgent = async () => {
    if (!maForm.headline.trim() || !maForm.primary_text.trim() || !maForm.call_to_action.trim()) {
      toast.error("Add a headline, primary text and call-to-action to run the evaluation.");
      return;
    }
    setMaRunning(true);
    setMaResult(null);
    try {
      let body;
      if (maMode === "existing") {
        body = {
          mode: "existing",
          campaign_id: maCampaign,
          objective: maForm.objective,
          bidding_strategy: maForm.bidding_strategy,
          geos: toList(maForm.geos),
          age_range: maForm.age_range || undefined,
          interests: toList(maForm.interests),
          job_titles: toList(maForm.job_titles),
          headline: maForm.headline,
          primary_text: maForm.primary_text,
          call_to_action: maForm.call_to_action,
          media_type: maForm.media_type,
          media_url: maForm.media_url || undefined,
          landing_page_url: maForm.landing_page_url || undefined,
        };
      } else {
        body = {
          mode: "custom",
          campaign_name: maForm.campaign_name || "New Campaign",
          objective: maForm.objective,
          platforms: maForm.platforms,
          total_budget: parseFloat(maForm.total_budget) || undefined,
          daily_budget: parseFloat(maForm.daily_budget) || undefined,
          bidding_strategy: maForm.bidding_strategy,
          geos: toList(maForm.geos),
          age_range: maForm.age_range || undefined,
          interests: toList(maForm.interests),
          job_titles: toList(maForm.job_titles),
          headline: maForm.headline,
          primary_text: maForm.primary_text,
          call_to_action: maForm.call_to_action,
          media_type: maForm.media_type,
          media_url: maForm.media_url || undefined,
          landing_page_url: maForm.landing_page_url || undefined,
        };
      }

      const resp = await fetch(`${API}/simulator/evaluate`, {
        method: "POST", credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      let data = {};
      try { data = await resp.json(); } catch { data = {}; }
      if (resp.ok) {
        setMaResult(data.simulation);
        toast.success("Multi-agent evaluation complete!");
      } else {
        const d = data?.detail;
        let msg = "Evaluation failed";
        if (typeof d === "string") msg = d;
        else if (d?.details?.[0]?.message) msg = d.details[0].message;
        else if (d?.error) msg = d.error;
        toast.error(msg);
      }
    } catch { toast.error("Evaluation failed"); }
    finally { setMaRunning(false); }
  };

  // ---------- classic derived ----------
  const sim = result?.simulation;
  const metrics = sim?.predicted_metrics;
  const audience = sim?.audience_reactions;
  const budgetScenarios = sim?.budget_scenarios || [];

  // ---------- multi-agent derived ----------
  const maMetrics = maResult?.metrics;
  const maRisk = maResult?.wasted_spend_risk;
  const maInsights = maResult?.agent_insights || [];
  const maRecs = maResult?.recommendations || [];
  const avgRating = maInsights.length
    ? Math.round(maInsights.reduce((s, a) => s + (Number(a.rating) || 0), 0) / maInsights.length)
    : 0;

  const inputCls = "bg-black/50 border-white/10 text-white";

  return (
    <DashboardLayout user={user}>
      <div data-testid="simulator-page" className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
            <FlaskConical className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h1 className="font-heading text-2xl text-white font-bold">AI Simulator</h1>
            <p className="text-zinc-400 text-sm">Simulate customer reactions to your campaigns before you spend</p>
          </div>
        </div>

        {/* Engine toggle */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <button
            data-testid="engine-multiagent-btn"
            onClick={() => setEngine("multiagent")}
            className={cn(
              "flex-1 text-left rounded-xl border p-4 transition-colors",
              engine === "multiagent" ? "border-violet-500/60 bg-violet-500/10" : "border-white/10 bg-[#121212] hover:border-white/20"
            )}
          >
            <div className="flex items-center gap-2 mb-1">
              <Bot className={cn("w-4 h-4", engine === "multiagent" ? "text-violet-300" : "text-zinc-400")} />
              <span className="text-white text-sm font-semibold">Multi-Agent Evaluation</span>
              <Badge className="bg-violet-500/15 text-violet-300 border border-violet-500/20 text-[10px]">New</Badge>
            </div>
            <p className="text-zinc-400 text-xs">10 expert AI personas grade your creative, targeting & budget — with a wasted-spend risk breakdown.</p>
          </button>
          <button
            data-testid="engine-classic-btn"
            onClick={() => setEngine("classic")}
            className={cn(
              "flex-1 text-left rounded-xl border p-4 transition-colors",
              engine === "classic" ? "border-amber-500/60 bg-amber-500/10" : "border-white/10 bg-[#121212] hover:border-white/20"
            )}
          >
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className={cn("w-4 h-4", engine === "classic" ? "text-amber-300" : "text-zinc-400")} />
              <span className="text-white text-sm font-semibold">Classic Simulation</span>
            </div>
            <p className="text-zinc-400 text-xs">Single-model prediction of daily metrics, budget scenarios & audience behavior.</p>
          </button>
        </div>

        {/* ================= MULTI-AGENT ENGINE ================= */}
        {engine === "multiagent" && (
          <>
            <Card className="bg-[#121212] border-white/10 mb-6">
              <CardContent className="p-6">
                <div className="flex gap-3 mb-4">
                  <Button size="sm" data-testid="ma-mode-existing-btn" onClick={() => setMaMode("existing")} className={maMode === "existing" ? "bg-white text-black" : "bg-white/5 text-zinc-400"}>Existing Campaign</Button>
                  <Button size="sm" data-testid="ma-mode-custom-btn" onClick={() => setMaMode("custom")} className={maMode === "custom" ? "bg-white text-black" : "bg-white/5 text-zinc-400"}>Custom Campaign</Button>
                </div>

                {/* Campaign / platform + budget */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {maMode === "existing" ? (
                    <div className="space-y-2 md:col-span-2">
                      <Label className="text-zinc-400">Select Campaign (budget & platform auto-mapped)</Label>
                      <Select value={maCampaign} onValueChange={handleSelectMaCampaign}>
                        <SelectTrigger data-testid="ma-campaign-select" className={inputCls}><SelectValue placeholder="Choose a campaign..." /></SelectTrigger>
                        <SelectContent className="bg-[#18181B] border-white/10">
                          {campaigns.map((c) => (
                            <SelectItem key={c.campaign_id} value={c.campaign_id} className="text-white">
                              {PLATFORMS[c.platform]?.name?.split(" ")[0] || c.platform} · {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Campaign Name</Label>
                        <Input data-testid="ma-campaign-name" value={maForm.campaign_name} onChange={(e) => setMaForm({ ...maForm, campaign_name: e.target.value })} placeholder="e.g. Summer SaaS Launch" className={inputCls} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Platforms</Label>
                        <div className="flex flex-wrap gap-2 pt-1">
                          {availablePlatforms.map(([k, v]) => (
                            <button
                              key={k}
                              type="button"
                              data-testid={`ma-platform-${k}`}
                              onClick={() => togglePlatform(k)}
                              className={cn(
                                "px-3 py-1.5 rounded-full text-xs border transition-colors",
                                maForm.platforms.includes(k) ? "bg-white text-black border-white" : "bg-white/5 text-zinc-400 border-white/10 hover:border-white/25"
                              )}
                            >
                              {v.name}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Total Budget ($)</Label>
                        <Input type="number" data-testid="ma-total-budget" value={maForm.total_budget} onChange={(e) => setMaForm({ ...maForm, total_budget: e.target.value })} className={inputCls} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Daily Budget ($)</Label>
                        <Input type="number" data-testid="ma-daily-budget" value={maForm.daily_budget} onChange={(e) => setMaForm({ ...maForm, daily_budget: e.target.value })} className={inputCls} />
                      </div>
                    </>
                  )}

                  <div className="space-y-2">
                    <Label className="text-zinc-400">Objective</Label>
                    <Select value={maForm.objective} onValueChange={(v) => setMaForm({ ...maForm, objective: v })}>
                      <SelectTrigger data-testid="ma-objective" className={inputCls}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#18181B] border-white/10">
                        {OBJECTIVES.map((o) => <SelectItem key={o} value={o} className="text-white capitalize">{o.replace("_", " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Bidding Strategy</Label>
                    <Select value={maForm.bidding_strategy} onValueChange={(v) => setMaForm({ ...maForm, bidding_strategy: v })}>
                      <SelectTrigger data-testid="ma-bidding" className={inputCls}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#18181B] border-white/10">
                        {BIDDING_STRATEGIES.map((b) => <SelectItem key={b} value={b} className="text-white capitalize">{b.replace(/_/g, " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Audience */}
                <p className="text-zinc-300 text-xs font-semibold uppercase tracking-wide mb-2 mt-2">Target Audience</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Geos (comma-separated)</Label>
                    <Input data-testid="ma-geos" value={maForm.geos} onChange={(e) => setMaForm({ ...maForm, geos: e.target.value })} placeholder="US, CA, UK" className={inputCls} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Age Range</Label>
                    <Input data-testid="ma-age" value={maForm.age_range} onChange={(e) => setMaForm({ ...maForm, age_range: e.target.value })} placeholder="25-45" className={inputCls} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Interests (comma-separated)</Label>
                    <Input data-testid="ma-interests" value={maForm.interests} onChange={(e) => setMaForm({ ...maForm, interests: e.target.value })} placeholder="SaaS, marketing, analytics" className={inputCls} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Job Titles (comma-separated)</Label>
                    <Input data-testid="ma-jobtitles" value={maForm.job_titles} onChange={(e) => setMaForm({ ...maForm, job_titles: e.target.value })} placeholder="Marketing Manager, CMO" className={inputCls} />
                  </div>
                </div>

                {/* Creative */}
                <p className="text-zinc-300 text-xs font-semibold uppercase tracking-wide mb-2 mt-2">Ad Creative</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Headline</Label>
                    <Input data-testid="ma-headline" value={maForm.headline} onChange={(e) => setMaForm({ ...maForm, headline: e.target.value })} placeholder="Cut Wasted Ad Spend by 40%" className={inputCls} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Call To Action</Label>
                    <Input data-testid="ma-cta" value={maForm.call_to_action} onChange={(e) => setMaForm({ ...maForm, call_to_action: e.target.value })} placeholder="Start Free Trial" className={inputCls} />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-zinc-400">Primary Text</Label>
                    <Textarea data-testid="ma-primary-text" value={maForm.primary_text} onChange={(e) => setMaForm({ ...maForm, primary_text: e.target.value })} placeholder="Describe your offer, value proposition and proof..." className={cn(inputCls, "min-h-[80px]")} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Media Type</Label>
                    <Select value={maForm.media_type} onValueChange={(v) => setMaForm({ ...maForm, media_type: v })}>
                      <SelectTrigger data-testid="ma-media-type" className={inputCls}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#18181B] border-white/10">
                        {MEDIA_TYPES.map((m) => <SelectItem key={m} value={m} className="text-white capitalize">{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Landing Page URL</Label>
                    <Input data-testid="ma-landing-url" value={maForm.landing_page_url} onChange={(e) => setMaForm({ ...maForm, landing_page_url: e.target.value })} placeholder="https://yoursite.com/offer" className={inputCls} />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-zinc-400">Media URL (optional)</Label>
                    <Input data-testid="ma-media-url" value={maForm.media_url} onChange={(e) => setMaForm({ ...maForm, media_url: e.target.value })} placeholder="https://yoursite.com/ad-image.png" className={inputCls} />
                  </div>
                </div>

                <Button
                  data-testid="ma-run-btn"
                  onClick={runMultiAgent}
                  disabled={maRunning || (maMode === "existing" && !maCampaign)}
                  className="mt-5 bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white font-bold h-11 px-6"
                >
                  {maRunning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}
                  {maRunning ? "Consulting 10 agents..." : "Run Multi-Agent Evaluation"}
                </Button>
              </CardContent>
            </Card>

            {maResult && (
              <motion.div data-testid="ma-results" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                {/* Summary + metrics */}
                <div className="flex items-center gap-4 p-4 bg-[#121212] border border-white/10 rounded-xl">
                  <div className={cn("w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold",
                    avgRating >= 70 ? "bg-emerald-500/20 text-emerald-400" : avgRating >= 40 ? "bg-amber-500/20 text-amber-400" : "bg-red-500/20 text-red-400"
                  )} data-testid="ma-avg-score">
                    {avgRating}
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-semibold">Multi-Agent Consensus Score</p>
                    <p className="text-zinc-400 text-sm">Averaged across {maInsights.length} expert evaluators · {maMetrics?.confidence_score != null ? `${Math.round(maMetrics.confidence_score * 100)}% confidence` : "—"}</p>
                  </div>
                </div>

                {maMetrics && (
                  <>
                    <h3 className="text-white font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4 text-blue-400" /> Predicted Performance</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <MetricCard icon={TrendingUp} label="Predicted ROAS" value={`${maMetrics.predicted_roas}x`} color={maMetrics.predicted_roas >= 2 ? "text-emerald-400" : "text-amber-400"} />
                      <MetricCard icon={DollarSign} label="Predicted CAC" value={`$${maMetrics.predicted_cac}`} color="text-amber-400" />
                      <MetricCard icon={MousePointer} label="Predicted CTR" value={`${maMetrics.predicted_ctr}`} color="text-cyan-400" />
                      <MetricCard icon={Target} label="Predicted CPC" value={`$${maMetrics.predicted_cpc}`} color="text-blue-400" />
                      <MetricCard icon={Shield} label="Confidence" value={`${Math.round((maMetrics.confidence_score || 0) * 100)}%`} color="text-violet-400" />
                    </div>
                  </>
                )}

                {/* Wasted spend risk */}
                {maRisk && (
                  <div data-testid="ma-wasted-risk" className="bg-gradient-to-br from-red-500/10 to-transparent border border-red-500/20 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                        <span className="text-white font-semibold">Wasted-Spend Risk</span>
                      </div>
                      <div className="text-right">
                        <p className="text-red-400 text-2xl font-bold">${(maRisk.total_recoverable_usd || 0).toLocaleString()}</p>
                        <p className="text-zinc-500 text-xs">potentially recoverable</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(maRisk.breakdown || {}).map(([k, v]) => (
                        <div key={k} className="bg-[#121212] border border-white/10 rounded-lg p-3">
                          <p className="text-zinc-400 text-xs mb-1">{RISK_LABELS[k] || k}</p>
                          <p className="text-white text-lg font-bold">${(v || 0).toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Agent insights */}
                {maInsights.length > 0 && (
                  <>
                    <h3 className="text-white font-semibold flex items-center gap-2"><Bot className="w-4 h-4 text-violet-400" /> Agent Panel — {maInsights.length} Expert Evaluations</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {maInsights.map((a, i) => <AgentCard key={a.agent_id} agent={a} index={i} />)}
                    </div>
                  </>
                )}

                {/* Recommendations */}
                {maRecs.length > 0 && (
                  <Card data-testid="ma-recommendations" className="bg-[#121212] border-white/10">
                    <CardHeader className="pb-2"><CardTitle className="text-white text-sm flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-400" /> Recommendations</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                      {maRecs.map((r, i) => <p key={`ma-rec-${i}`} className="text-zinc-400 text-sm flex gap-2"><span className="text-emerald-400">-</span>{r}</p>)}
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            )}
          </>
        )}

        {/* ================= CLASSIC ENGINE ================= */}
        {engine === "classic" && (
          <>
            <Card className="bg-[#121212] border-white/10 mb-6">
              <CardContent className="p-6">
                <div className="flex gap-3 mb-4">
                  <Button size="sm" onClick={() => setMode("existing")} className={mode === "existing" ? "bg-white text-black" : "bg-white/5 text-zinc-400"}>Existing Campaign</Button>
                  <Button size="sm" onClick={() => setMode("custom")} className={mode === "custom" ? "bg-white text-black" : "bg-white/5 text-zinc-400"}>Custom Campaign</Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Platform</Label>
                    <Select value={platform} onValueChange={setPlatform}>
                      <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#18181B] border-white/10">
                        {availablePlatforms.map(([k, v]) => <SelectItem key={k} value={k} className="text-white">{v.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {mode === "existing" ? (
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Select Campaign</Label>
                      <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
                        <SelectTrigger className={inputCls}><SelectValue placeholder="Choose campaign..." /></SelectTrigger>
                        <SelectContent className="bg-[#18181B] border-white/10">
                          {campaigns.filter((c) => c.platform === platform).map((c) => (
                            <SelectItem key={c.campaign_id} value={c.campaign_id} className="text-white">{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Campaign Name</Label>
                        <Input value={form.campaign_name} onChange={(e) => setForm({ ...form, campaign_name: e.target.value })} placeholder="e.g. Summer Sale 2026" className={inputCls} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Target Audience</Label>
                        <Input value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} placeholder="e.g. SaaS marketers, 25-45, US" className={inputCls} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Daily Budget ($)</Label>
                        <Input type="number" value={form.daily_budget} onChange={(e) => setForm({ ...form, daily_budget: e.target.value })} placeholder="50" className={inputCls} />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label className="text-zinc-400">Ad Creative / Copy</Label>
                        <Textarea value={form.creative_text} onChange={(e) => setForm({ ...form, creative_text: e.target.value })} placeholder="Enter your ad headline, description, and CTA..." className={cn(inputCls, "min-h-[80px]")} />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label className="text-zinc-400">Campaign Description (optional)</Label>
                        <Input value={form.campaign_description} onChange={(e) => setForm({ ...form, campaign_description: e.target.value })} placeholder="Brief description of campaign goals..." className={inputCls} />
                      </div>
                    </>
                  )}
                </div>

                <Button onClick={runSimulation} disabled={running || (mode === "existing" && !selectedCampaign)} className="mt-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold h-11 px-6">
                  {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FlaskConical className="w-4 h-4 mr-2" />}
                  {running ? "Simulating..." : "Run Simulation"}
                </Button>
              </CardContent>
            </Card>

            {sim && !sim.parse_error && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                <div className="flex items-center gap-4 p-4 bg-[#121212] border border-white/10 rounded-xl">
                  <div className={cn("w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold",
                    sim.overall_score >= 70 ? "bg-emerald-500/20 text-emerald-400" : sim.overall_score >= 40 ? "bg-amber-500/20 text-amber-400" : "bg-red-500/20 text-red-400"
                  )}>
                    {sim.overall_score}
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-semibold">{result.platform} Simulation</p>
                    <p className="text-zinc-400 text-sm">{sim.summary}</p>
                  </div>
                </div>

                {metrics && (
                  <>
                    <h3 className="text-white font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4 text-blue-400" /> Predicted Daily Metrics</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <MetricCard icon={Eye} label="Impressions" value={metrics.daily_impressions?.toLocaleString()} color="text-blue-400" />
                      <MetricCard icon={MousePointer} label="Clicks" value={metrics.daily_clicks?.toLocaleString()} color="text-cyan-400" sub={`CTR: ${metrics.ctr_percent}%`} />
                      <MetricCard icon={ShoppingCart} label="Conversions" value={metrics.daily_conversions?.toLocaleString()} color="text-emerald-400" sub={`CVR: ${metrics.conversion_rate_percent}%`} />
                      <MetricCard icon={DollarSign} label="Revenue" value={`$${metrics.daily_revenue?.toLocaleString()}`} color="text-amber-400" sub={`CPA: $${metrics.cpa_dollars}`} />
                      <MetricCard icon={TrendingUp} label="ROAS" value={`${metrics.predicted_roas}x`} color={metrics.predicted_roas >= 2 ? "text-emerald-400" : "text-amber-400"} sub={`QS: ${metrics.quality_score}/10`} />
                    </div>
                  </>
                )}

                {budgetScenarios.length > 0 && (
                  <>
                    <h3 className="text-white font-semibold flex items-center gap-2"><DollarSign className="w-4 h-4 text-amber-400" /> Budget Scenarios</h3>
                    <div className="bg-[#121212] border border-white/10 rounded-xl p-4 h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={budgetScenarios}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                          <XAxis dataKey="budget" stroke="#71717a" tick={{ fill: "#71717a", fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                          <YAxis stroke="#71717a" tick={{ fill: "#71717a", fontSize: 11 }} />
                          <Tooltip contentStyle={{ background: "#121212", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", color: "#fff" }} />
                          <Bar dataKey="roas" name="ROAS" radius={[4, 4, 0, 0]}>
                            {budgetScenarios.map((entry, i) => <Cell key={`cell-${i}`} fill={entry.roas >= 2 ? "#10B981" : entry.roas >= 1 ? "#F59E0B" : "#EF4444"} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                )}

                {audience && (
                  <>
                    <h3 className="text-white font-semibold flex items-center gap-2"><Users className="w-4 h-4 text-violet-400" /> Audience Behavior Analysis</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <MetricCard icon={Target} label="Sentiment" value={`${audience.sentiment_score}/100`} color="text-violet-400" />
                      <MetricCard icon={Zap} label="Engagement" value={audience.engagement_likelihood} color="text-blue-400" />
                      <MetricCard icon={MousePointer} label="Click Probability" value={audience.click_probability} color="text-cyan-400" />
                      <MetricCard icon={ShoppingCart} label="Purchase Intent" value={audience.purchase_intent} color="text-emerald-400" />
                    </div>

                    {audience.audience_segments?.length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {audience.audience_segments.map((seg, i) => (
                          <div key={`seg-${i}-${seg.segment}`} className="bg-[#121212] border border-white/10 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-white text-sm font-medium">{seg.segment}</span>
                              <Badge className={cn("text-xs", seg.response === "Positive" ? "bg-emerald-500/10 text-emerald-400" : seg.response === "Negative" ? "bg-red-500/10 text-red-400" : "bg-zinc-500/10 text-zinc-400")}>{seg.response}</Badge>
                            </div>
                            <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                              <div className="h-full bg-violet-500 rounded-full" style={{ width: `${seg.likelihood_percent}%` }} />
                            </div>
                            <p className="text-zinc-400 text-xs mt-1">{seg.likelihood_percent}% likelihood</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {audience.ad_fatigue_risk && (
                      <div className={cn("p-3 rounded-lg flex items-center gap-2 text-sm",
                        audience.ad_fatigue_risk === "High" ? "bg-red-500/10 text-red-400" : audience.ad_fatigue_risk === "Medium" ? "bg-amber-500/10 text-amber-400" : "bg-emerald-500/10 text-emerald-400"
                      )}>
                        <AlertTriangle className="w-4 h-4" />
                        Ad Fatigue Risk: {audience.ad_fatigue_risk}
                      </div>
                    )}
                  </>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {sim.recommendations?.length > 0 && (
                    <Card className="bg-[#121212] border-white/10">
                      <CardHeader className="pb-2"><CardTitle className="text-white text-sm flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-400" /> Recommendations</CardTitle></CardHeader>
                      <CardContent className="space-y-2">
                        {sim.recommendations.map((r, i) => <p key={`rec-${i}`} className="text-zinc-400 text-sm flex gap-2"><span className="text-emerald-400">-</span>{r}</p>)}
                      </CardContent>
                    </Card>
                  )}
                  {sim.risk_factors?.length > 0 && (
                    <Card className="bg-[#121212] border-white/10">
                      <CardHeader className="pb-2"><CardTitle className="text-white text-sm flex items-center gap-2"><Shield className="w-4 h-4 text-amber-400" /> Risk Factors</CardTitle></CardHeader>
                      <CardContent className="space-y-2">
                        {sim.risk_factors.map((r, i) => <p key={`risk-${i}`} className="text-zinc-400 text-sm flex gap-2"><span className="text-amber-400">-</span>{r}</p>)}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </motion.div>
            )}

            {sim?.parse_error && (
              <div className="bg-[#121212] border border-white/10 rounded-xl p-6">
                <p className="text-zinc-400 text-sm whitespace-pre-wrap">{sim.raw_response}</p>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
